import { Button } from '@/components/design-system/Button';
import { FormInput } from '@/features/form/inputs/FormInput';
import { FormPassword } from '@/features/form/inputs/FormPassword';
import { cn } from '@/utils/cn';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Lock, Mail, User } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const acceptInvitationSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string()
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
  });

type AcceptInvitationFormValues = z.infer<typeof acceptInvitationSchema>;

interface AcceptInvitationFormProps {
  onSubmit: (data: Omit<AcceptInvitationFormValues, 'confirmPassword'>) => Promise<boolean>;
  loading?: boolean;
  email?: string | null;
}

export const AcceptInvitationForm = ({ onSubmit, loading, email }: AcceptInvitationFormProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid }
  } = useForm<AcceptInvitationFormValues>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: ''
    },
    mode: 'onChange'
  });

  const password = watch('password');

  const handleFormSubmit = async (data: AcceptInvitationFormValues) => {
    const { confirmPassword: _, ...submitData } = data;
    await onSubmit(submitData);
  };

  const isDisabled = loading === true || !isValid;
  const isLoading = loading === true;

  const passwordMeetsRequirements = password.length >= 8;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4">
      {email !== null && email !== undefined && (
        <div className="relative">
          <Mail className="text-typography-900/45 absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2" />
          <CheckCircle className="text-success-400 absolute top-1/2 right-3 z-10 size-4 -translate-y-1/2" />
          <input
            type="email"
            value={email}
            readOnly
            tabIndex={-1}
            className="border-success-400 bg-success-200 text-typography-900/65 h-10 w-81 cursor-not-allowed rounded-md border pr-10 pl-10 text-sm outline-none"
            aria-label={t('acceptInvitation.emailLabel')}
          />
        </div>
      )}

      <FormInput
        control={control}
        name="firstName"
        leftIcon={User}
        placeholder="acceptInvitation.firstNamePlaceholder"
        autoComplete="off"
        disabled={isLoading}
        hideErrorMessage={false}
      />

      <FormInput
        control={control}
        name="lastName"
        leftIcon={User}
        placeholder="acceptInvitation.lastNamePlaceholder"
        autoComplete="off"
        disabled={isLoading}
        hideErrorMessage={false}
      />

      <div className="flex flex-col gap-1">
        <FormPassword
          control={control}
          name="password"
          leftIcon={Lock}
          placeholder="acceptInvitation.passwordPlaceholder"
          autoComplete="new-password"
          hideErrorMessage={false}
        />
        <div className="flex items-center gap-1.5 text-xs">
          <span
            className={cn(
              'transition-colors duration-200',
              password.length === 0
                ? 'text-typography-900/45'
                : passwordMeetsRequirements
                  ? 'text-success-400'
                  : 'text-error-500'
            )}
          >
            {passwordMeetsRequirements && '✓'} {t('acceptInvitation.passwordRequirements')}
          </span>
        </div>
      </div>

      <FormPassword
        control={control}
        name="confirmPassword"
        leftIcon={Lock}
        placeholder="acceptInvitation.confirmPasswordPlaceholder"
        autoComplete="new-password"
        hideErrorMessage={false}
      />

      <Button
        type="submit"
        loading={loading}
        disabled={isDisabled}
        className={cn(
          'h-10 w-81 border text-base transition-all duration-200',
          isLoading && 'cursor-wait opacity-60',
          !isLoading && isValid
            ? 'bg-primary border-primary text-typography hover:bg-primary/90 hover:text-typography/90'
            : 'bg-secondary-900/4 border-border-100 text-secondary-900/25 cursor-not-allowed'
        )}
      >
        {t('acceptInvitation.submit')}
      </Button>
    </form>
  );
};
